/**
 * CashFlowFilter Component
 *
 * Company + year + month filters, a Monthly/Weekly view toggle, Apply and
 * Export actions, plus an inline Tweaks section (layout, chart style, show charts).
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Button,
    Collapse,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    HStack,
    IconButton,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Switch,
    Text,
    Tooltip,
    VStack,
    ButtonGroup,
} from '@chakra-ui/react';
import { FiSearch, FiDownload, FiSettings } from 'react-icons/fi';
import api from '../../auth/apiClient';
import { CashFlowFilters, CompanyOption, TweaksSettings, SummaryLayout, ChartStyle } from '../types';
import { getMonthOptions, getYearOptions } from '../utils';

export type CashFlowViewMode = 'monthly' | 'weekly';

interface CashFlowFilterProps {
    filters: CashFlowFilters;
    onApplyFilters: (filters: CashFlowFilters) => void;
    onExport?: () => void;
    viewMode: CashFlowViewMode;
    onViewModeChange: (mode: CashFlowViewMode) => void;
    tweaks: TweaksSettings;
    onTweaksChange: (next: TweaksSettings) => void;
}

const CashFlowFilter: React.FC<CashFlowFilterProps> = ({
    filters,
    onApplyFilters,
    onExport,
    viewMode,
    onViewModeChange,
    tweaks,
    onTweaksChange,
}) => {
    const [localFilters, setLocalFilters] = useState<CashFlowFilters>(filters);
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [showTweaks, setShowTweaks] = useState(false);

    const monthOptions = getMonthOptions();
    const yearOptions = getYearOptions();

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const response = await api.get('/inspeksi/options');
                setCompanies(response.data?.companies || []);
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        };
        fetchCompanies();
    }, []);

    const handleChange = useCallback(
        (field: keyof CashFlowFilters, value: string) => {
            setLocalFilters((prev) => ({ ...prev, [field]: Number(value) }));
        },
        [],
    );

    const handleApply = useCallback(() => {
        onApplyFilters(localFilters);
    }, [localFilters, onApplyFilters]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleApply();
    };

    const updateTweak = (partial: Partial<TweaksSettings>) =>
        onTweaksChange({ ...tweaks, ...partial });

    return (
        <Box bg="white" p={{ base: 4, md: 5 }} borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
            <VStack spacing={4} align="stretch">
                {/* Header row */}
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                    <Text fontSize="md" fontWeight="bold" color="gray.700">
                        Filter Cash Flow
                    </Text>
                    <HStack spacing={2}>
                        <ButtonGroup size="sm" isAttached variant="outline">
                            <Button
                                onClick={() => onViewModeChange('monthly')}
                                bg={viewMode === 'monthly' ? 'customGreen.500' : 'white'}
                                color={viewMode === 'monthly' ? 'white' : 'gray.700'}
                                _hover={{ bg: viewMode === 'monthly' ? 'customGreen.600' : 'gray.100' }}
                            >
                                Monthly
                            </Button>
                            <Button
                                onClick={() => onViewModeChange('weekly')}
                                bg={viewMode === 'weekly' ? 'customGreen.500' : 'white'}
                                color={viewMode === 'weekly' ? 'white' : 'gray.700'}
                                _hover={{ bg: viewMode === 'weekly' ? 'customGreen.600' : 'gray.100' }}
                            >
                                Weekly
                            </Button>
                        </ButtonGroup>
                        {onExport && (
                            <Tooltip label="Export ke Excel" hasArrow>
                                <IconButton
                                    aria-label="Export data"
                                    icon={<FiDownload />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="green"
                                    onClick={onExport}
                                />
                            </Tooltip>
                        )}
                        <Tooltip label="Pengaturan tampilan" hasArrow>
                            <IconButton
                                aria-label="Tweaks"
                                icon={<FiSettings />}
                                size="sm"
                                variant={showTweaks ? 'solid' : 'ghost'}
                                colorScheme="gray"
                                onClick={() => setShowTweaks((v) => !v)}
                            />
                        </Tooltip>
                    </HStack>
                </Flex>

                {/* Filter selects */}
                <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>Company</FormLabel>
                        <Select
                            size="sm"
                            borderRadius="lg"
                            value={localFilters.company_id}
                            onChange={(e) => handleChange('company_id', e.target.value)}
                            onKeyDown={handleKeyPress}
                        >
                            {companies.length === 0 && (
                                <option value={localFilters.company_id}>Company {localFilters.company_id}</option>
                            )}
                            {companies.map((company) => (
                                <option key={company.id} value={company.id}>
                                    {company.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>Tahun</FormLabel>
                        <Select
                            size="sm"
                            borderRadius="lg"
                            value={localFilters.year}
                            onChange={(e) => handleChange('year', e.target.value)}
                            onKeyDown={handleKeyPress}
                        >
                            {yearOptions.map((year) => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>Bulan</FormLabel>
                        <Select
                            size="sm"
                            borderRadius="lg"
                            value={localFilters.month}
                            onChange={(e) => handleChange('month', e.target.value)}
                            onKeyDown={handleKeyPress}
                        >
                            {monthOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Apply button */}
                <Flex justify={{ base: 'stretch', md: 'flex-end' }} direction={{ base: 'column', sm: 'row' }}>
                    <Button
                        leftIcon={<FiSearch />}
                        onClick={handleApply}
                        size="sm"
                        bg="customGreen.500"
                        color="white"
                        _hover={{ bg: 'customGreen.600' }}
                        borderRadius="lg"
                        flex={{ base: 1, sm: 'initial' }}
                    >
                        Terapkan Filter
                    </Button>
                </Flex>

                {/* Tweaks section (collapsible) */}
                <Collapse in={showTweaks} animateOpacity>
                    <Divider mb={3} />
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                        {/* Layout Summary */}
                        <Box>
                            <Text fontSize="10px" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                Layout Summary
                            </Text>
                            <RadioGroup
                                value={tweaks.summaryLayout}
                                onChange={(v) => updateTweak({ summaryLayout: v as SummaryLayout })}
                            >
                                <Stack spacing={1}>
                                    <Radio value="period-cards" size="sm"><Text fontSize="sm">Period Cards</Text></Radio>
                                    <Radio value="hero" size="sm"><Text fontSize="sm">Hero KPI</Text></Radio>
                                    <Radio value="compact" size="sm"><Text fontSize="sm">Compact Strip</Text></Radio>
                                </Stack>
                            </RadioGroup>
                        </Box>

                        {/* Chart Style */}
                        <Box>
                            <Text fontSize="10px" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                Chart Style
                            </Text>
                            <RadioGroup
                                value={tweaks.chartStyle}
                                onChange={(v) => updateTweak({ chartStyle: v as ChartStyle })}
                            >
                                <Stack spacing={1}>
                                    <Radio value="bars" size="sm"><Text fontSize="sm">Bars</Text></Radio>
                                    <Radio value="lines" size="sm"><Text fontSize="sm">Lines</Text></Radio>
                                    <Radio value="area" size="sm"><Text fontSize="sm">Area</Text></Radio>
                                </Stack>
                            </RadioGroup>
                        </Box>

                        {/* Display options */}
                        <Box>
                            <Text fontSize="10px" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                Tampilan
                            </Text>
                            <HStack justify="space-between" py={1}>
                                <Text fontSize="sm" color="gray.700">Tampilkan Charts</Text>
                                <Switch
                                    colorScheme="green"
                                    size="sm"
                                    isChecked={tweaks.showCharts}
                                    onChange={(e) => updateTweak({ showCharts: e.target.checked })}
                                />
                            </HStack>
                        </Box>
                    </Grid>
                </Collapse>
            </VStack>
        </Box>
    );
};

export default CashFlowFilter;
